import type { Translation } from '../../types/i18n'

export const en: Translation = {
  announcement:
    'Made to order · 3 days’ notice · $50 minimum · Pickup in Chambly or delivery',
  nav: {
    home: 'Home',
    cacao: 'The Art of Cacao',
    creations: 'Our Creations',
    ingredients: 'Ingredients',
    giftCard: 'Gift card',
    recipes: 'Recipes',
    events: 'Corporate & Events',
    contact: 'Contact',
    cart: 'Cart',
    order: 'Order',
  },
  hero: {
    eyebrow: 'Artisanal chocolaterie · Chambly',
    titleLead: 'Les Artisans Sucrés',
    titleAccent: 'The Art of Cacao',
    subtitle:
      'Cacao with character, elegant creations and a story of resilience and passion.',
    ctaPrimary: 'Discover our creations',
    ctaSecondary: 'The Art of Cacao',
  },
  cacao: {
    eyebrow: 'Our philosophy',
    titleLead: 'The Art of',
    titleAccent: 'Cacao',
    intro:
      'Cacao is far more than an ingredient: it is a living legacy, a sacred fruit that ancient civilisations treasured. Every bean carries a story, an energy and a nobility that we honour with respect and delicacy.',
    benefitsTitle: 'Before it becomes chocolate, cacao is a powerful food',
    benefits: [
      'Rich in antioxidants',
      'Good for the heart',
      'A natural mood lifter',
      'A source of essential minerals',
      'Supports memory and focus',
    ],
    philosophyTitle: 'Our credo',
    philosophyQuote: 'Less sugar, more cacao.',
    philosophyText:
      'The higher the cacao percentage, the more authentic, nourishing and genuinely flavourful the chocolate. Our creations are designed to be wholesome, refined and deeply true.',
    heritageTitle: 'A tribute to our roots',
    heritageText:
      'Peruvian alfajores — classic or coated in quality chocolate — are a tribute to those roots. A meeting of tradition and modernity, shaped by hand, with intention, precision and love.',
  },
  promo: {
    badge: 'Welcome offer',
    title: 'Join now to receive your 15% off for your first online order.',
    text: 'And unlock more discounts, recipes, and exclusive chocolatey goodness.',
    placeholder: 'Your email address',
    cta: 'Join now',
    success: 'Thank you! Your 15% code is on its way by email.',
  },
  story: {
    eyebrow: 'My story',
    titleLead: 'A passion for',
    titleAccent: 'real chocolate',
    strugglesIntro: 'My beginnings were not easy:',
    struggles: [
      'The chocolate refused to temper',
      'The moulds would not release',
      'The colourings kept sticking',
    ],
    paragraphs: [
      'But I never stopped. I practised, failed, started again… over and over. My philosophy is clear: no obstacle can stop passion, discipline and the desire to reach perfection.',
      'Today I create chocolates that are unique, elegant and full of emotion. At Les Artisans Sucrés we work with high-percentage chocolate to offer products that are healthier, more authentic and rich in real flavour.',
      'We also offer our Peruvian alfajores, classic or coated in quality chocolate — a tribute to my roots, revisited with a modern and refined touch.',
      'Les Artisans Sucrés is the expression of my story, my resilience and my deep love for the art of cacao.',
    ],
    closing:
      'Every product is shaped by hand, with intention, precision and love.',
    signatureName: 'Sara',
    signatureRole: 'Master Chocolatier',
  },
  products: {
    eyebrow: 'Our Collections',
    titleLead: 'Indulgent',
    titleAccent: 'Creations',
    addToCart: 'Add to cart',
    added: 'Added!',
    quantity: 'Quantity',
    chooseOption: 'Choose your format',
    saveForLater: 'Save for later',
    savedLabel: 'Saved',
    orderNow: 'Place your order',
    from: 'from',
    categoriesLabel: 'Categories',
    categories: [
      { id: 'all', name: 'View all' },
      { id: 'alfajores', name: 'Alfajores' },
      { id: 'chocolats', name: 'Fine chocolates' },
      { id: 'tablettes', name: 'Bars' },
      { id: 'croustillants', name: 'Crunchy' },
      { id: 'cakepops', name: 'Cake pops' },
    ],
    items: [
      {
        id: 'alfajores',
        name: 'Peruvian Alfajores',
        subtitle: 'Our Signature',
        description:
          'Two tender cookies embracing a generous heart of artisanal dulce de leche and homemade strawberry jam, dusted with icing sugar. A tribute to my Peruvian roots, in boxes of 12 to 60.',
        tags: ['Dulce de leche', 'Strawberry jam', '12 to 60'],
      },
      {
        id: 'alfajores-enrobes',
        name: 'Chocolate-Coated Alfajores',
        subtitle: 'Tradition & Cacao',
        description:
          'Our Peruvian alfajores fully coated in quality chocolate — 70% dark, white or milk — and finished with almonds, pecans or cranberries. Box of 12.',
        tags: ['Dark 70%', 'White', 'Milk'],
      },
      {
        id: 'bonbons',
        name: 'Praline Chocolates',
        subtitle: 'The Elegance of Cacao',
        description:
          'Hand-painted praline bonbons with a thin shell and a silky centre. Wrapped one by one, available in boxes of 12, 24 and 36 chocolates.',
        tags: ['Praline', 'Hand-painted', '12 · 24 · 36'],
      },
      {
        id: 'pralines',
        name: 'Homemade Nut Pralines',
        subtitle: 'Homemade',
        description:
          'Our homemade nut pralines: almonds, hazelnuts and golden raisins on a disc of white, milk or dark chocolate. Sold in packs of 4.',
        tags: ['Almonds', 'Hazelnuts', '$5 per pack of 4'],
      },
      {
        id: 'fruits',
        name: 'Fruit Chocolates',
        subtitle: 'Passion fruit · Lucuma · Strawberry · Baileys',
        description:
          'Bonbons with a flowing centre of passion fruit, lucuma, strawberry or Baileys, in a hand-painted marbled shell. We work with 100% fresh ingredients, with no artificial flavours.',
        tags: ['Passion fruit', 'Lucuma', 'Strawberry', 'Baileys'],
      },
      {
        id: 'tablettes',
        name: 'Chocolate Bars',
        subtitle: 'Multiple Flavours',
        description:
          'Our bars are available in multiple flavours: white chocolate, milk chocolate and 70% dark chocolate, topped with dried fruit, nuts or candied peel.',
        tags: ['White', 'Milk', 'Dark 70%'],
      },
      {
        id: 'pretzels',
        name: 'Chocolate-Coated Pretzels',
        subtitle: 'Sweet & Salty',
        description:
          'Pretzels coated in milk chocolate and 70% dark chocolate: an amalgam of crunchy, sweet and salty that is impossible to stop after the first bite.',
        tags: ['Crunchy', 'Sweet & salty', '10 pieces · 13 $'],
      },
      {
        id: 'cakepops',
        name: 'Cake Pops',
        subtitle: 'Festive Sweetness',
        description:
          'Soft cake bites coated in glossy chocolate and decorated by hand. Assorted chocolate and vanilla, sold per piece.',
        tags: ['Assorted', 'Chocolate & vanilla', '3 $ each'],
      },
    ],
  },
  ingredients: {
    eyebrow: 'Our ingredients',
    titleLead: 'Quality, before',
    titleAccent: 'everything else',
    intro:
      'A chocolate is never better than its ingredients. We choose every component for its real taste, never for convenience.',
    freshNote: 'We work with 100% fresh ingredients.',
    items: [
      {
        title: 'Cacao Barry cacao pastilles',
        text:
          'We work exclusively with Cacao Barry couverture pastilles, a French benchmark recognised for the purity of its beans and the finesse of its fluidity when tempering.',
      },
      {
        title: 'High cacao percentage',
        text:
          'Our chocolates favour a high cacao percentage: less sugar, more real flavour and a frank aromatic profile.',
      },
      {
        title: 'Fruit, nuts and pure butter',
        text:
          'Almonds, hazelnuts, pecans, cranberries, fresh fruit and pure butter — no artificial flavours, no fat substitutes.',
      },
    ],
  },
  giftCard: {
    eyebrow: 'Gift card',
    titleLead: 'Give the gift of',
    titleAccent: 'indulgence',
    intro:
      'Not sure what to choose? Give a Les Artisans Sucrés gift card, valid on all our creations, for pickup as well as delivery.',
    amountsLabel: 'Available amounts',
    cta: 'Order a gift card',
    note:
      'Gift cards are sent by email and never expire. Message us or write by email to personalise yours.',
  },
  orderInfo: {
    eyebrow: 'How to order',
    titleLead: 'Fresh, made',
    titleAccent: 'to order',
    intro:
      'We keep no stock: every box is prepared especially for you, once your order comes in.',
    cards: [
      {
        title: 'Made to order · 3 days’ notice',
        text:
          'All our products are made to order. Please allow a minimum of 3 days before your desired date so we can prepare everything by hand.',
      },
      {
        title: '$50 minimum order',
        text:
          'A minimum of $50 is required for every order, for both pickup and delivery.',
      },
      {
        title: 'Order by DM',
        text:
          'For events and corporate gifts, message us directly on Instagram, Facebook or TikTok.',
      },
    ],
    pickupTitle: 'Pickup in Chambly',
    deliveryTitle: 'Delivery',
    deliveryNote:
      'Delivery fees are calculated based on distance. Contact us for the exact rate.',
    dmTitle: 'Order by direct message',
    dmText:
      'Events, weddings and corporate gifts: send us a DM and we will build your project to measure.',
  },
  recipes: {
    eyebrow: 'Our workshop at your home',
    titleLead: 'Indulgent',
    titleAccent: 'Recipes',
    intro:
      'A few of our favourite recipes to make with family. Take your time: patience is the first ingredient of craftsmanship.',
    ingredientsLabel: 'Ingredients',
    stepsLabel: 'Method',
    items: [
      {
        id: 'dulce',
        title: 'Homemade Dulce de Leche',
        time: '2 hr 30',
        yield: 'about 400 g',
        description:
          'The heart of our alfajores. A slow, creamy milk caramel with deep aroma.',
        ingredients: [
          '1 L whole milk',
          '300 g sugar',
          '1 split vanilla pod',
          '1 pinch baking soda',
        ],
        steps: [
          'Bring the milk, sugar and vanilla to a gentle simmer in a heavy-bottomed pot.',
          'Add the baking soda off the heat, then return to low heat.',
          'Reduce for 2 hours, stirring regularly, until it turns amber.',
          'Remove the vanilla, let cool and refrigerate for up to 2 weeks.',
        ],
      },
      {
        id: 'alfajores-maison',
        title: 'Cornstarch Alfajores',
        time: '1 hr 15',
        yield: '12 alfajores',
        description:
          'Ultra-tender cookies that melt on the tongue, filled with dulce de leche.',
        ingredients: [
          '300 g cornstarch',
          '150 g flour',
          '200 g softened unsalted butter',
          '100 g icing sugar',
          '3 egg yolks',
          'Zest of one lemon',
          'Dulce de leche and shredded coconut',
        ],
        steps: [
          'Cream the butter with the icing sugar, then fold in the yolks and zest.',
          'Add the sifted cornstarch and flour without overworking the dough.',
          'Roll out to 1 cm, cut discs and chill for 30 minutes.',
          'Bake 10 to 12 minutes at 170 °C — the cookies should stay pale.',
          'Fill generously with dulce de leche and roll the edges in coconut.',
        ],
      },
      {
        id: 'ganache',
        title: 'Grand Cru 70% Ganache',
        time: '20 min',
        yield: '20 truffles',
        description:
          'Three ingredients, one silky texture. Little sugar, plenty of cacao.',
        ingredients: [
          '200 g 70% dark chocolate',
          '150 ml 35% cream',
          '30 g unsalted butter',
          'Cocoa powder for coating',
        ],
        steps: [
          'Finely chop the chocolate and place it in a bowl.',
          'Bring the cream to a simmer and pour it over the chocolate in three additions.',
          'Stir from the centre outwards, then fold in the butter.',
          'Chill for 3 hours, shape into balls and roll them in cocoa.',
        ],
      },
      {
        id: 'cakepops',
        title: 'Chocolate Cake Pops',
        time: '1 hr + chilling',
        yield: '18 cake pops',
        description:
          'Soft cake bites coated in chocolate, ready to decorate however you like.',
        ingredients: [
          '250 g crumbled chocolate cake',
          '80 g ganache or cream cheese',
          '300 g couverture chocolate',
          'Cake pop sticks',
          'Decorations: edible gold, pearls, rose-tinted chocolate',
        ],
        steps: [
          'Mix the crumbled cake with the ganache until you get a smooth paste.',
          'Shape into 30 g balls and chill for 1 hour.',
          'Dip the tip of each stick in melted chocolate, then insert it into a ball.',
          'Coat fully in tempered chocolate and let the excess drip off.',
          'Decorate immediately and let set upright.',
        ],
      },
    ],
  },
  events: {
    eyebrow: 'Corporate & Events',
    titleLead: 'Sweetness for your',
    titleAccent: 'special occasions',
    intro:
      'We create custom orders for businesses and events: weddings, christenings, birthdays, client thank-yous and employee gifts. Order directly by direct message.',
    offerings: [
      {
        title: 'Corporate gifts',
        text:
          'Elegant boxes for your clients and teams, with the option to customise the packaging and add your card or logo.',
      },
      {
        title: 'Weddings & celebrations',
        text:
          'Guest favours, dessert tables, decorated cake pops and thank-you boxes matched to your palette.',
      },
      {
        title: 'Large quantities',
        text:
          'Alfajores, chocolates and cake pops in volume, on a schedule agreed in advance to guarantee perfect freshness.',
      },
      {
        title: 'Custom flavours',
        text:
          'We develop bespoke fillings and coatings with you to create a sweet that is uniquely yours.',
      },
    ],
    ctaTitle: 'Let’s talk about your project',
    ctaText:
      'Send us a DM or call us with your dates, quantities and ideas. Please allow a minimum of 3 days and a $50 minimum order.',
    cta: 'Request a quote',
    dmCta: 'Order by DM',
  },
  contact: {
    eyebrow: 'Contact',
    titleLead: 'Let’s talk',
    titleAccent: 'indulgence',
    intro:
      'A question, a special order, an event project? Write, call or send us a direct message.',
    pickupLabel: 'Pickup',
    deliveryLabel: 'Delivery',
    phoneLabel: 'Phone',
    emailLabel: 'Email',
    ordersLabel: 'Orders',
    ordersValue: 'Made to order · 3 days’ notice · $50 minimum',
    followTitle: 'Follow us',
    form: {
      title: 'Send us a message',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Message',
      messagePlaceholder:
        'Tell us what you would like, the quantity and your desired date…',
      send: 'Send',
      note: 'We usually reply within 24 hours.',
    },
  },
  cart: {
    eyebrow: 'Your order',
    titleLead: 'Your',
    titleAccent: 'Cart',
    empty: 'Your cart is empty for now.',
    emptyCta: 'See our creations',
    quantity: 'Quantity',
    savedTitle: 'Saved for later',
    savedEmpty: 'No saved products for now.',
    moveToCart: 'Add to cart',
    remove: 'Remove',
    subtotal: 'Subtotal',
    notice: 'All our products are made to order: please allow 3 days’ notice.',
    deliveryNote:
      'Pickup in Chambly, or delivery to Brossard, Longueuil, Candiac, Saint-Hubert, Saint-Jean-sur-Richelieu and Carignan — fee based on distance.',
    minimumWarning: '$50 minimum order — add {amount} $ more to check out.',
    minimumMet: 'Minimum order reached.',
    checkout: 'Pay now',
    securePayment:
      'Secure payment processed by Shopify. You will be redirected to checkout to pay by card, Apple Pay or Google Pay.',
    paymentMethodsLabel: 'Accepted payment methods',
    orderByDm: 'Order by DM',
    continueShopping: 'Continue shopping',
    each: 'each',
  },
  refund: {
    title: 'Refund policy',
    paragraphs: [
      'Any change of mind regarding the order results in the direct loss of the product.',
      'For this reason, once your order is confirmed, it cannot be cancelled or returned.',
    ],
  },
  footer: {
    tagline:
      'The art of cacao, shaped by hand. Pickup in Chambly or delivery across the South Shore.',
    contactTitle: 'Contact',
    navigationTitle: 'Navigation',
    legalTitle: 'Information',
    rights: 'All rights reserved.',
    refundLink: 'Refund policy',
  },
}
